import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { InstalledApplication } from '@rocksoft/mdm-sdk-v2';
import { Observable, Subject, merge } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AppleCommandProgressService } from '../../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../../utilities/command-spinner/command-spinner.component';
import { ConfigurationProfile } from '../../../viewmodels/apple/configuration-profile.model';
import { AppLockProfile } from '../../../viewmodels/apple/profiles/app-lock-profile/app-lock-profile.model';
import { CommandProgress } from '../../../viewmodels/apple/signalr/command-context.model';

@Component({
  selector: 'ml-app-lock-modal',
  templateUrl: './app-lock-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommandSpinnerComponent, ReactiveFormsModule, FaIconComponent],
})
export class AppLockModalComponent implements OnInit {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly appleMdmService = inject(AppleMdmService);
  private readonly commandProgressService = inject(AppleCommandProgressService);
  public readonly modal = inject(NgbActiveModal);

  public app: InstalledApplication = { udid: '' };
  public udid = '';

  public appLockForm: UntypedFormGroup;
  public showOptions = false;
  public showUserOptions = false;
  appLockProfile: AppLockProfile = new AppLockProfile();

  private readonly _commandProgress = new Subject<CommandProgress>();
  public commandProgress$: Observable<CommandProgress>;

  constructor() {
    this.createForm();
  }

  ngOnInit(): void {
    this.commandProgress$ = merge(
      this._commandProgress,
      this.commandProgressService.commandsOfRequestType(this.udid, 'InstallProfile')
    );
    this.initForm();
  }

  private createForm() {
    this.appLockForm = this.fb.group({
      disableTouch: '',
      disableDeviceRotation: '',
      disableVolumeButtons: '',
      disableRingerSwitch: '',
      disableSleepWakeButton: '',
      disableAutoLock: '',
      enableVoiceOver: '',
      enableZoom: '',
      enableInvertColors: '',
      enableAssistiveTouch: '',
      enableSpeakSelection: '',
      enableMonoAudio: '',
      voiceOver: '',
      zoom: '',
      invertColors: '',
      assistiveTouch: '',
    });
  }

  populateFromForm(): void {
    this.appLockProfile.app.options.disableTouch = this.appLockForm.get('disableTouch')?.value ?? false;
    this.appLockProfile.app.options.disableDeviceRotation =
      this.appLockForm.get('disableDeviceRotation')?.value ?? false;
    this.appLockProfile.app.options.disableVolumeButtons = this.appLockForm.get('disableVolumeButtons')?.value ?? false;
    this.appLockProfile.app.options.disableRingerSwitch = this.appLockForm.get('disableRingerSwitch')?.value ?? false;
    this.appLockProfile.app.options.disableSleepWakeButton =
      this.appLockForm.get('disableSleepWakeButton')?.value ?? false;
    this.appLockProfile.app.options.disableAutoLock = this.appLockForm.get('disableAutoLock')?.value ?? false;
    this.appLockProfile.app.options.enableVoiceOver = this.appLockForm.get('enableVoiceOver')?.value ?? false;
    this.appLockProfile.app.options.enableZoom = this.appLockForm.get('enableZoom')?.value ?? false;
    this.appLockProfile.app.options.enableInvertColors = this.appLockForm.get('enableInvertColors')?.value ?? false;
    this.appLockProfile.app.options.enableAssistiveTouch = this.appLockForm.get('enableAssistiveTouch')?.value ?? false;
    this.appLockProfile.app.options.enableSpeakSelection = this.appLockForm.get('enableSpeakSelection')?.value ?? false;
    this.appLockProfile.app.options.enableMonoAudio = this.appLockForm.get('enableMonoAudio')?.value ?? false;
    this.appLockProfile.app.userEnabledOptions.voiceOver = this.appLockForm.get('voiceOver')?.value ?? false;
    this.appLockProfile.app.userEnabledOptions.zoom = this.appLockForm.get('zoom')?.value ?? false;
    this.appLockProfile.app.userEnabledOptions.invertColors = this.appLockForm.get('invertColors')?.value ?? false;
    this.appLockProfile.app.userEnabledOptions.assistiveTouch = this.appLockForm.get('assistiveTouch')?.value ?? false;
  }

  onSubmit() {
    const _udid = this.udid;
    if (!_udid) return;
    if (this.appLockForm.status !== 'VALID') {
      return;
    }

    this.populateFromForm();

    const command: CommandProgress = {
      udid: _udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'InstallProfile',
    };
    this._commandProgress.next(command);

    const configurationProfile = new ConfigurationProfile();
    configurationProfile.payloadContent = [this.appLockProfile];
    configurationProfile.payloadIdentifier = 'com.methylium.applock';
    configurationProfile.payloadOrganization = 'Methylium B.V.';
    configurationProfile.payloadDescription = 'This profile has pre-configured settings for app lock.';
    // HACK, this way we store which app (bundleIdentifier) is being put in SAM
    // The 'payloadDisplayName' should be the same when it is being generated in a API
    configurationProfile.payloadDisplayName = `App Lock for: ${this.app.identifier ?? ''}`; // Can't be required: "This key will always be present on iOS/tvOS but may be missing on macOS."
    configurationProfile.payloadUUID = '87b06894-4394-4149-8794-8d1ec89be02b';

    this.appLockProfile.payloadDescription = 'App Lock configures the device to only allow the use of a single app.';
    this.appLockProfile.payloadDisplayName = `App Lock - ${this.app.name ?? ''}`;
    this.appLockProfile.payloadIdentifier = 'com.methylium.applock.test';
    this.appLockProfile.payloadOrganization = 'Methylium B.V.';
    this.appLockProfile.payloadVersion = 1;
    this.appLockProfile.payloadUUID = '87b06894-4394-4149-8794-8d1ec89be02a';

    this.appleMdmService
      .installProfile(_udid, configurationProfile)
      .pipe(
        finalize(() => {
          this.appLockForm.markAsPristine();
        })
      )
      .subscribe();
  }

  private initForm(): void {
    this.appLockProfile.app.identifier = this.app.identifier ?? ''; // TODO: Methylium.Apple.Mdm.Commands.InstalledApplication Identifier should be required.

    this.appLockForm.reset({
      disableTouch: this.appLockProfile.app.options.disableTouch,
      disableDeviceRotation: this.appLockProfile.app.options.disableDeviceRotation,
      disableVolumeButtons: this.appLockProfile.app.options.disableVolumeButtons,
      disableRingerSwitch: this.appLockProfile.app.options.disableRingerSwitch,
      disableSleepWakeButton: this.appLockProfile.app.options.disableSleepWakeButton,
      disableAutoLock: this.appLockProfile.app.options.disableAutoLock,
      enableVoiceOver: this.appLockProfile.app.options.enableVoiceOver,
      enableZoom: this.appLockProfile.app.options.enableZoom,
      enableInvertColors: this.appLockProfile.app.options.enableInvertColors,
      enableAssistiveTouch: this.appLockProfile.app.options.enableAssistiveTouch,
      enableSpeakSelection: this.appLockProfile.app.options.enableSpeakSelection,
      enableMonoAudio: this.appLockProfile.app.options.enableMonoAudio,
      voiceOver: this.appLockProfile.app.userEnabledOptions.voiceOver,
      zoom: this.appLockProfile.app.userEnabledOptions.zoom,
      invertColors: this.appLockProfile.app.userEnabledOptions.invertColors,
      assistiveTouch: this.appLockProfile.app.userEnabledOptions.assistiveTouch,
    });
  }
}
